import { useContext, useEffect, useState } from 'preact/hooks';
import { SessionContext } from '../app.jsx';
import { get, post, del } from '../api.js';
import { Link, navigate } from '../router.js';
import { Tag, PaidToggle } from '../ui/Tag.jsx';
import { Contact } from './DepartureGuide.jsx';
import { fmtRange } from '../../lib/dates.js';

// SC-006 Tura: the group with paid switches, the equipment taken, and putting guides on or off.
export function DepartureOffice({ params }) {
  const id = Number(params.id);
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);
  const [guideError, setGuideError] = useState(null);
  const [busy, setBusy] = useState(false);
  const { tick } = useContext(SessionContext);

  const load = () => get(`/departures/${id}`).then((r) => { setData(r); setError(null); }, (e) => setError(e));
  useEffect(() => { load(); }, [id, tick]);
  // /ture/:id#vodici from the calendar: scroll to the guides once they are on the page
  useEffect(() => { if (data && location.hash) document.getElementById(location.hash.slice(1))?.scrollIntoView(); }, [!!data]);

  const guideAct = async (fn) => {
    setBusy(true);
    setGuideError(null);
    try {
      await fn();
      await load();
    } catch (e) {
      setGuideError(e.message);
    }
    setBusy(false);
  };
  const removeGuide = (g) => confirm(`Skloniti ${g.name} sa ove ture?`) && guideAct(() => del(`/departures/${id}/guides/${g.id}`));
  const removeTour = async () => {
    if (!confirm('Obrisati ovu turu?')) return;
    setBusy(true);
    try {
      await del(`/departures/${id}`);
      navigate('/ture');
      return;
    } catch (e) {
      setError(e);
    }
    setBusy(false);
  };
  const paidChanged = ({ id: bid, paid }) => setData((x) => ({ ...x, bookings: x.bookings.map((b) => (b.id === bid ? { ...b, paid } : b)) }));

  if (!data) {
    if (error?.status === 404) return <p class="empty">Te ture nema. <Link href="/ture">Na ture</Link></p>;
    return error ? <p class="alert" role="alert">{error.message}</p> : null;
  }
  const { departure: d, bookings, people, takes, guides, candidates } = data;
  const free = candidates.filter((c) => !c.onThis);

  return (
    <>
      <div class="page-head">
        <div>
          <h1>{d.tour.name}</h1>
          <p class="sub">{fmtRange(d.start_date, d.end_date)}</p>
        </div>
        <div class="actions">
          {bookings.length === 0 && guides.length === 0 && (
            <button class="btn-quiet btn-danger" disabled={busy} onClick={removeTour}>Obriši turu</button>
          )}
          <Link href={`/rezervacije/nova?tourId=${d.tour.id}&date=${d.start_date}`} class="btn">Nova rezervacija</Link>
        </div>
      </div>

      <section class="section" aria-labelledby="grupa">
        <h2 id="grupa" class="sr-only">Grupa</h2>
        {bookings.length === 0 ? <p class="empty">Još nema rezervacija za ovu turu. Upiši prvu: Nova rezervacija.</p> : (
          <table class="stack">
            <thead>
              <tr><th>Ko</th><th>Kontakt</th><th class="num">Ljudi</th><th>Napomena</th><th>Plaćeno</th></tr>
            </thead>
            <tbody>
              {bookings.map((b) => (
                <tr key={b.id}>
                  <td data-label="Ko"><Link href={`/rezervacije/${b.id}`}>{b.name}</Link></td>
                  <td data-label="Kontakt"><Contact value={b.contact} /></td>
                  <td data-label="Ljudi" class="num">{b.people}</td>
                  <td data-label="Napomena">{b.note}</td>
                  <td data-label="Plaćeno"><PaidToggle booking={b} onChange={paidChanged} onError={(m) => setError({ message: m })} /></td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr><td colSpan={5} class="total num">Ukupno: {people} ljudi</td></tr>
            </tfoot>
          </table>
        )}
        {error && <p class="alert" role="alert">{error.message}</p>}
      </section>

      <section class="section" aria-labelledby="oprema-na-turi">
        <h2 id="oprema-na-turi">Oprema na turi</h2>
        {takes.length === 0 ? <p class="empty">Još nema upisane opreme.</p> : (
          <ul class="plain-list rows">
            {takes.map((k) => <li key={k.id}>{`${k.guide.name} — ${k.item.name} × ${k.qty}`}</li>)}
          </ul>
        )}
      </section>

      <section class="section" id="vodici" aria-labelledby="dodeli-vodica">
        <h2 id="dodeli-vodica">Dodeli vodiča</h2>
        {guideError && <p class="alert" role="alert">{guideError}</p>}
        {candidates.length === 0 && (
          <p class="empty">Još nema vodiča. Napravi ih u <Link href="/vodici">Vodiči</Link>.</p>
        )}
        <ul class="plain-list rows guides">
          {guides.map((g) => (
            <li key={g.id} class="guide-row" data-testid={`assigned-${g.id}`}>
              <strong>{g.name}</strong>
              <span class="guide-state">Na turi</span>
              <button class="btn-quiet" disabled={busy} onClick={() => removeGuide(g)}>Skloni</button>
            </li>
          ))}
          {free.map((c) => (
            <li key={c.id} class="guide-row" data-testid={`candidate-${c.id}`}>
              <span>{c.name}</span>
              {c.busy
                ? <span class="guide-state"><Tag kind="zauzeto">Zauzeto</Tag> {c.busy.tour}, {fmtRange(c.busy.start_date, c.busy.end_date)}</span>
                : <span class="guide-state"><Tag kind="slobodno">Slobodno</Tag></span>}
              {!c.busy && (
                <button class="btn-quiet" disabled={busy} onClick={() => guideAct(() => post(`/departures/${id}/guides`, { guideId: c.id }))}>Dodeli</button>
              )}
            </li>
          ))}
        </ul>
      </section>
    </>
  );
}
